import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import EbdaEduNavbar from "components/navbars/EbdaEduNavbar";
import { CLASS_STATUS } from "constants/constants";
import { useLanguage } from "context/LanguageContext";
import { useTheme } from "context/ThemeContext";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { createClass, createClassRoom, deleteExistingCourse, editExistingCourse } from "services/admins";
import {
  fetchWatomsRelatedClassRooms,
  fetchWatomsRelatedCoursesDetailed,
  fetchWatomsRelatedSpecializations,
  fetchWatomsRelatedVtcs,
} from "services/watoms/watomsData";
import { extractDate } from "utils/extractDate";

const EbdaEduWatomsCourseManagement = () => {
  const { language } = useLanguage();
  const { darkMode } = useTheme();

  const [courses, setCourses] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [filteredSpecializations, setFilteredSpecializations] = useState([]);
  const [vtcs, setVtcs] = useState([]);
  const [classRooms, setClassRooms] = useState([]);
  const [filteredClassRooms, setFilteredClassRooms] = useState([]);

  const [openIdx, setOpenIdx] = useState(null);
  const [editingCourseId, setEditingCourseId] = useState(null);

  const [openForm, setOpenForm] = useState(null); // "classroom" | "class" | null
  // ------- Add Classroom -------
  const [newClassRoomName, setNewClassRoomName] = useState("");
  const [newClassRoomOrgId, setNewClassRoomOrgId] = useState("");
  const [newRoomType, setNewRoomType] = useState("class");

  // ------- Add Class -------
  const [newClassName, setNewClassName] = useState("");
  const [newClassRoomId, setNewClassRoomId] = useState("");
  const [newSpecializationId, setNewSpecializationId] = useState("");
  const [newStartDate, setNewStartDate] = useState("");
  const [newEndDate, setNewEndDate] = useState("");
  const [newHours, setNewHours] = useState("");
  const [newMale, setNewMale] = useState("");
  const [newFemale, setNewFemale] = useState("");
  const [newStatus, setNewStatus] = useState("");

  const [editedStatus, setEditedStatus] = useState("");
  const [editedMale, setEditedMale] = useState("");
  const [editedFemale, setEditedFemale] = useState("");
  const [editedHours, setEditedHours] = useState("");
  const [editedSpecialization, setEditedSpecialization] = useState("");
  const [vtcFilterId, setVtcFilterId] = useState("");
  const [editedClassRoom, setEditedClassRoom] = useState("");
  const [editedStartDate, setEditedStartDate] = useState("");
  const [editedEndDate, setEditedEndDate] = useState("");

  useEffect(() => {
    const loadAll = async () => {
      const [coursesRes, specsRes, vtcsRes, classRoomsRes] = await Promise.all([
        fetchWatomsRelatedCoursesDetailed(),
        fetchWatomsRelatedSpecializations(),
        fetchWatomsRelatedVtcs(),
        fetchWatomsRelatedClassRooms(),
      ]);

      setCourses(coursesRes);
      setSpecializations(specsRes);
      setFilteredSpecializations(specsRes)
      setVtcs(vtcsRes);
      setClassRooms(classRoomsRes);
      setFilteredClassRooms(classRoomsRes)
    };

    loadAll();
  }, []);

  const startEdit = (course) => {
    setEditingCourseId(course.id);

    setEditedStatus(course?.status || "");
    setEditedFemale(course?.no_of_female_students ?? "");
    setEditedMale(course?.no_of_male_students ?? "");
    setEditedHours(course?.hours ?? "");
    setEditedSpecialization(course?.specialization_id ?? course?.specialization?.id ?? "");
    setEditedStartDate(course?.start_date ? extractDate(course.start_date) : "");
    setEditedEndDate(course?.end_date ? extractDate(course.end_date) : "");
    setVtcFilterId(course?.classRoom?.organization_id ?? course?.classRoom?.organization?.id ?? "");
    setEditedClassRoom(course?.classRoom_id ?? course?.classRoom?.id ?? "");
  };

  const cancelEdit = () => {
    setEditingCourseId(null);
    setEditedStatus("");
    setEditedFemale("");
    setEditedMale("");
    setEditedHours("");
    setEditedSpecialization("");
    setEditedStartDate("");
    setEditedEndDate("");
    setVtcFilterId("");
    setEditedClassRoom("");
  };

  const confirmEdit = async (course) => {
    try {
      await editExistingCourse(course.id, {
        status: editedStatus,
        no_of_female_students: Number(editedFemale) || 0,
        no_of_male_students: Number(editedMale) || 0,
        hours: Number(editedHours) || 0,
        specialization_id: editedSpecialization ? Number(editedSpecialization) : null,
        classRoom_id: editedClassRoom ? Number(editedClassRoom) : null,
        start_date: editedStartDate || null,
        end_date: editedEndDate || null,
      });

      const selectedClassRoom = classRooms.find((cr) => cr.id === Number(editedClassRoom));
      const selectedSpec = specializations.find((s) => s.id === Number(editedSpecialization));

      setCourses((prev) =>
        prev.map((c) =>
          c.id === course.id
            ? {
              ...c,
              status: editedStatus,
              no_of_female_students: Number(editedFemale) || 0,
              no_of_male_students: Number(editedMale) || 0,
              hours: Number(editedHours) || 0,
              specialization_id: editedSpecialization ? Number(editedSpecialization) : null,
              specialization: selectedSpec || c.specialization,
              classRoom_id: editedClassRoom ? Number(editedClassRoom) : null,
              classRoom: selectedClassRoom || c.classRoom,
              start_date: editedStartDate || null,
              end_date: editedEndDate || null,
            }
            : c
        )
      );

      toast.success(language ? "Updated" : "تم التحديث");
      cancelEdit();
    } catch (err) {
      toast.error(language ? "Update failed" : "فشل التحديث");
    }
  };

  const handleDeleteCourse = async (course) => {
    try {
      await deleteExistingCourse(course.id);
      toast.success(language ? "Deleted" : "تم الحذف");
      setCourses((prev) => prev.filter((c) => c.id !== course.id));
      if (editingCourseId === course.id) cancelEdit();
    } catch (err) {
      toast.error(language ? "Delete failed" : "فشل الحذف");
    }
  };

  const handleAddClassRoom = async (e) => {
    e.preventDefault();

    if (!newClassRoomName || !newClassRoomOrgId) {
      toast.error(language ? "Fill all fields" : "املأ كل الحقول");
      return;
    }

    try {
      const created = await createClassRoom({
        Name: newClassRoomName,
        organization_id: Number(newClassRoomOrgId),
        room_type: newRoomType,
      });

      setClassRooms((prev) => [created, ...prev]);
      setFilteredClassRooms((prev) => [created, ...prev]);

      setNewClassRoomName("");
      setNewClassRoomOrgId("");

      toast.success(language ? "Classroom added" : "تم إضافة القاعة");
    } catch (err) {
      toast.error(language ? "Failed to add classroom" : "فشل إضافة القاعة");
    }
  };

  const handleAddClass = async (e) => {
    e.preventDefault();

    if (!newClassName || !newClassRoomId) {
      toast.error(language ? "Fill required fields" : "املأ الحقول المطلوبة");
      return;
    }

    try {

      const created = await createClass({
        name: newClassName,
        classRoom_id: Number(newClassRoomId),
        specialization_id: newSpecializationId ? Number(newSpecializationId) : null,
        start_date: newStartDate || null,
        end_date: newEndDate || null,
        hours: Number(newHours) || 0,
        no_of_male_students: Number(newMale) || 0,
        no_of_female_students: Number(newFemale) || 0,
        status: newStatus || "ongoing",
      });
      
      const createdCourse = {
        ...created,
        classRoom: classRooms.find((cr) => cr.id === Number(newClassRoomId)) || null,
        specialization: specializations.find((s) => s.id === Number(newSpecializationId)) || null,
        sessions: [],
      };
      
      setCourses((prev) => [createdCourse, ...prev]);
      
      // reset
      setNewClassName("");
      setNewClassRoomId("");
      setNewSpecializationId("");
      setNewStartDate("");
      setNewEndDate("");
      setNewHours("");
      setNewMale("");
      setNewFemale("");
      setNewStatus("");
      
      toast.success(language ? "Course added" : "تم إضافة الدورة");
    } catch (err) {
      toast.error(language ? "Failed to add course" : "فشل إضافة الدورة");
    }
  };

  const filterClassRooms = async (value) => {
    setNewClassRoomOrgId(value);
    const filterClassRooms = classRooms.filter(c => c.organization_id === Number(value));
    setFilteredClassRooms(filterClassRooms);
    const filterSpecializations = specializations.filter(spec => spec.organizations?.some(org => org.id === Number(value)))
    setFilteredSpecializations(filterSpecializations )
  }

  return (
    <div className={`w-full min-h-screen flex flex-col items-center ${darkMode && "bg-watomsBlue text-white"}`}>
      <EbdaEduNavbar />
      {/* Add Forms (Accordion) */}
      <div className="w-[90%] mb-6 space-y-4" dir={language ? "ltr" : "rtl"}>

        {/* Add Classroom */}
        <div
          className={`rounded-2xl border shadow-sm overflow-hidden ${darkMode ? "bg-white text-black border-black/10" : "bg-white border-slate-200"
            }`}
        >
          {/* Header */}
          <button
            type="button"
            onClick={() => setOpenForm(openForm === "classroom" ? null : "classroom")}
            className="w-full px-4 py-3 flex items-center justify-between font-bold"
          >
            <span>{language ? "Add Lab / Workshop" : "إضافة معمل / ورشة"}</span>
            <span className={`transition-transform duration-300 ${openForm === "classroom" ? "rotate-180" : ""}`}>
              <FontAwesomeIcon icon={faChevronDown} className="text-slate-500" />
            </span>
          </button>

          {/* Body */}
          <div className={`transition-all duration-300 overflow-hidden ${openForm === "classroom" ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}`}>
            <form onSubmit={handleAddClassRoom} className="p-4 pt-0 space-y-3">
              <input
                className="w-full rounded-xl px-3 py-2 border border-slate-200 outline-none"
                placeholder={language ? "Name" : "الاسم"}
                value={newClassRoomName}
                onChange={(e) => setNewClassRoomName(e.target.value)}
              />

              {/* ✅ NEW: Room Type */}
              <select
                className="w-full rounded-xl px-3 py-2 border border-slate-200 outline-none"
                value={newRoomType}
                onChange={(e) => setNewRoomType(e.target.value)}
              >
                <option value="lab">{language ? "Lab" : "معمل"}</option>
                <option value="workshop">{language ? "Workshop" : "ورشة"}</option>
              </select>

              <select
                className="w-full rounded-xl px-3 py-2 border border-slate-200 outline-none"
                value={newClassRoomOrgId}
                onChange={(e) => setNewClassRoomOrgId(e.target.value)}
              >
                <option value="">{language ? "Select VTC" : "اختر المركز"}</option>
                {vtcs?.map((vtc) => (
                  <option key={vtc.id} value={vtc.id}>
                    {vtc.name}
                  </option>
                ))}
              </select>

              <button
                type="submit"
                className="w-full rounded-xl py-2 font-semibold bg-blue-600 text-white hover:bg-blue-700"
              >
                {language ? "Add Classroom" : "إضافة القاعة"}
              </button>
            </form>
          </div>
        </div>

        {/* Add Class */}
        <div
          className={`rounded-2xl border shadow-sm overflow-hidden ${darkMode ? "bg-white text-black border-black/10" : "bg-white border-slate-200"
            }`}
        >
          {/* Header */}
          <button
            type="button"
            onClick={() => setOpenForm(openForm === "class" ? null : "class")}
            className="w-full px-4 py-3 flex items-center justify-between font-bold"
          >
            <span>{language ? "Add Class" : "إضافة دورة"}</span>
            <span className={`transition-transform duration-300 ${openForm === "class" ? "rotate-180" : ""}`}>
              <FontAwesomeIcon icon={faChevronDown} className="text-slate-500" />
            </span>
          </button>

          {/* Body */}
          <div className={`transition-all duration-300 overflow-hidden ${openForm === "class" ? "max-h-[900px] opacity-100" : "max-h-0 opacity-0"}`}>
            <form onSubmit={handleAddClass} className="p-4 pt-0 space-y-3">
              <input
                className="w-full rounded-xl px-3 py-2 border border-slate-200 outline-none"
                placeholder={language ? "Class name" : "اسم الدورة"}
                value={newClassName}
                onChange={(e) => setNewClassName(e.target.value)}
              />

              <select
                className="w-full rounded-xl px-3 py-2 border border-slate-200 outline-none"
                value={newClassRoomOrgId}
                onChange={(e) => filterClassRooms(e.target.value)}
              >
                <option value="">{language ? "Select Vtc" : "اختر المركز"}</option>
                {vtcs?.map((vtc) => (
                  <option key={vtc.id} value={vtc.id}>
                    {vtc.name}
                  </option>
                ))}
              </select>

              <select
                className="w-full rounded-xl px-3 py-2 border border-slate-200 outline-none"
                value={newClassRoomId}
                onChange={(e) => setNewClassRoomId(e.target.value)}
              >
                <option value="">{language ? "Select Classroom" : "اختر القاعة"}</option>
                {filteredClassRooms?.map((cr) => (
                  <option key={cr.id} value={cr.id}>
                    {cr.Name}
                  </option>
                ))}
              </select>

              <select
                className="w-full rounded-xl px-3 py-2 border border-slate-200 outline-none"
                value={newSpecializationId}
                onChange={(e) => setNewSpecializationId(e.target.value)}
              >
                <option value="">{language ? "Select Specialization" : "اختر التخصص"}</option>
                {filteredSpecializations?.map((sp) => (
                  <option key={sp.id} value={sp.id}>
                    {sp.name}
                  </option>
                ))}
              </select>

              <div className="grid grid-cols-2 gap-3">
                <input
                  type="date"
                  className="w-full rounded-xl px-3 py-2 border border-slate-200 outline-none"
                  value={newStartDate}
                  onChange={(e) => setNewStartDate(e.target.value)}
                />
                <input
                  type="date"
                  className="w-full rounded-xl px-3 py-2 border border-slate-200 outline-none"
                  value={newEndDate}
                  onChange={(e) => setNewEndDate(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <input
                  className="w-full rounded-xl px-3 py-2 border border-slate-200 outline-none"
                  placeholder={language ? "Hours" : "الساعات"}
                  value={newHours}
                  onChange={(e) => setNewHours(e.target.value)}
                />
                <input
                  className="w-full rounded-xl px-3 py-2 border border-slate-200 outline-none"
                  placeholder={language ? "Male" : "ذكور"}
                  value={newMale}
                  onChange={(e) => setNewMale(e.target.value)}
                />
                <input
                  className="w-full rounded-xl px-3 py-2 border border-slate-200 outline-none"
                  placeholder={language ? "Female" : "إناث"}
                  value={newFemale}
                  onChange={(e) => setNewFemale(e.target.value)}
                />
              </div>

              <select
                className="w-full rounded-xl px-3 py-2 border border-slate-200 outline-none"
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
              >
                <option value="">{language ? "Select Status" : "اختر الحالة"}</option>
                {CLASS_STATUS?.map((st) => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </select>

              <button
                type="submit"
                className="w-full rounded-xl py-2 font-semibold bg-green-600 text-white hover:bg-green-700"
              >
                {language ? "Add Class" : "إضافة الدورة"}
              </button>
            </form>
          </div>
        </div>
      </div>

      <div className="w-[90%] mx-auto" dir={language ? "ltr" : "rtl"}>
        {/* Header */}
        <div
          className={`grid grid-cols-12 gap-4 font-bold text-center rounded-2xl px-4 py-4 ${darkMode ? "text-black bg-white" : "text-white bg-black"
            }`}
        >
          <p>#</p>
          <p>{language ? "Start date" : "موعد بدء الدورة"}</p>
          <p>{language ? "End date" : "موعد انتهاء الدورة"}</p>
          <p>{language ? "VTC" : "المركز"}</p>
          <p>{language ? "Classroom" : "القاعة"}</p>
          <p>{language ? "Specialization" : "التخصص"}</p>
          <p>{language ? "Trainer Name" : "اسم المدرب"}</p>
          <p>{language ? "Course hours" : "عدد ساعات الدورة"}</p>
          <p>{language ? "no of male trainees" : "عدد المتدربين الذكور"}</p>
          <p>{language ? "no of female trainees" : "عدد المتدربين الاناث"}</p>
          <p>{language ? "total trainees" : "اجمالي المتدربين"}</p>
          <p>{language ? "Status" : "الحالة"}</p>
        </div>

        {/* Rows */}
        <div className="mt-3 space-y-2">
          {courses?.map((course, idx) => {
            const isOpen = openIdx === idx;
            const isEditing = editingCourseId === course.id;

            const male = Number(course?.no_of_male_students) ?? 0;
            const female = Number(course?.no_of_female_students) ?? 0;

            return (
              <div key={course?.id ?? idx} className="space-y-2">
                {/* Row */}
                <div
                  className={`grid grid-cols-12 gap-4 items-center text-black text-center rounded-2xl px-4 py-3 transition cursor-pointer
                    ${darkMode ? "bg-white border border-black/10" : "bg-slate-400 border border-white/10"}
                    hover:shadow-md hover:scale-[1.01]`}
                  onClick={() => setOpenIdx(isOpen ? null : idx)}
                >
                  <p className="font-medium">{idx + 1}</p>

                  {/* Start date */}
                  <p className="font-medium" onClick={(e) => e.stopPropagation()}>
                    {isEditing ? (
                      <input
                        type="date"
                        className={`w-full rounded-lg px-2 py-1 outline-none border text-sm ${darkMode ? "bg-white text-black border-black/20" : "bg-white text-black border-black/20"
                          }`}
                        value={editedStartDate}
                        onChange={(e) => setEditedStartDate(e.target.value)}
                      />
                    ) : (
                      extractDate(course?.start_date) || "لا يوجد"
                    )}
                  </p>

                  {/* End date */}
                  <p className="font-medium" onClick={(e) => e.stopPropagation()}>
                    {isEditing ? (
                      <input
                        type="date"
                        className={`w-full rounded-lg px-2 py-1 outline-none border text-sm ${darkMode ? "bg-white text-black border-black/20" : "bg-white text-black border-black/20"
                          }`}
                        value={editedEndDate}
                        onChange={(e) => setEditedEndDate(e.target.value)}
                      />
                    ) : (
                      extractDate(course?.end_date) || "لا يوجد"
                    )}
                  </p>

                  {/* VTC (filter only) */}
                  <p className="font-medium" onClick={(e) => e.stopPropagation()}>
                    {isEditing ? (
                      <select
                        className={`w-full rounded-lg px-2 py-1 outline-none border text-sm
                          ${darkMode ? "bg-white text-black border-black/20" : "bg-white text-black border-black/20"}`}
                        value={vtcFilterId}
                        onChange={(e) => {
                          setVtcFilterId(e.target.value);
                          setEditedClassRoom(""); // ✅ filter changed, pick classroom again
                        }}
                      >
                        <option value="">{language ? "Select" : "اختر"}</option>
                        {vtcs?.map((vtc) => (
                          <option key={vtc.id} value={vtc.id}>
                            {vtc.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      course?.classRoom?.organization?.name || "لا يوجد"
                    )}
                  </p>

                  {/* Classroom (saved) */}
                  <p className="font-medium">
                    {isEditing ? (
                      <select
                        className={`w-full rounded-lg px-2 py-1 outline-none border text-sm
                          ${darkMode ? "bg-white text-black border-black/20" : "bg-white text-black border-black/20"}`}
                        value={editedClassRoom}
                        onChange={(e) => setEditedClassRoom(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="">{language ? "Select" : "اختر"}</option>
                        {classRooms
                          ?.filter((cr) => !vtcFilterId || Number(cr.organization_id) === Number(vtcFilterId))
                          .map((cr) => (
                            <option key={cr.id} value={cr.id}>
                              {cr.Name}
                            </option>
                          ))}
                      </select>
                    ) : (
                      course?.classRoom?.Name || "لا يوجد"
                    )}
                  </p>

                  {/* Specialization */}
                  <p className="font-medium">
                    {isEditing ? (
                      <select
                        className={`w-full rounded-lg px-2 py-1 outline-none border text-sm
                          ${darkMode ? "bg-white text-black border-black/20" : "bg-white text-black border-black/20"}`}
                        value={editedSpecialization}
                        onChange={(e) => setEditedSpecialization(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="">{language ? "Select" : "اختر"}</option>
                        {specializations?.map((spec) => (
                          <option key={spec.id} value={spec.id}>
                            {spec.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      course?.specialization?.name || "لا يوجد"
                    )}
                  </p>

                  {/* Trainer */}
                  <p className="font-medium">
                    {course?.sessions?.[0]?.teacher?.employee?.first_name || "لا يوجد"}{" "}
                    {course?.sessions?.[0]?.teacher?.employee?.middle_name || ""}{" "}
                    {course?.sessions?.[0]?.teacher?.employee?.last_name || ""}
                  </p>

                  {/* Hours */}
                  <p className="font-medium" onClick={(e) => e.stopPropagation()}>
                    {isEditing ? (
                      <input
                        className={`w-full rounded-lg px-2 py-1 outline-none border text-sm ${darkMode ? "bg-white text-black border-black/20" : "bg-white text-black border-black/20"
                          }`}
                        type="text"
                        value={editedHours}
                        onChange={(e) => setEditedHours(e.target.value)}
                      />
                    ) : (
                      course?.hours || "لا يوجد"
                    )}
                  </p>

                  {/* Male */}
                  <p className="font-medium" onClick={(e) => e.stopPropagation()}>
                    {isEditing ? (
                      <input
                        className={`w-full rounded-lg px-2 py-1 outline-none border text-sm ${darkMode ? "bg-white text-black border-black/20" : "bg-white text-black border-black/20"
                          }`}
                        type="text"
                        value={editedMale}
                        onChange={(e) => setEditedMale(e.target.value)}
                      />
                    ) : (
                      male
                    )}
                  </p>

                  {/* Female */}
                  <p className="font-medium" onClick={(e) => e.stopPropagation()}>
                    {isEditing ? (
                      <input
                        className={`w-full rounded-lg px-2 py-1 outline-none border text-sm ${darkMode ? "bg-white text-black border-black/20" : "bg-white text-black border-black/20"
                          }`}
                        type="text"
                        value={editedFemale}
                        onChange={(e) => setEditedFemale(e.target.value)}
                      />
                    ) : (
                      female
                    )}
                  </p>

                  {/* Total */}
                  <p className="font-medium">{male + female}</p>

                  {/* Status */}
                  <div className="font-medium" onClick={(e) => e.stopPropagation()}>
                    {isEditing ? (
                      <select
                        className={`w-full rounded-lg px-2 py-1 outline-none border text-sm
                          ${darkMode ? "bg-white text-black border-black/20" : "bg-white text-black border-black/20"}`}
                        value={editedStatus}
                        onChange={(e) => setEditedStatus(e.target.value)}
                      >
                        <option value="">{language ? "Select" : "اختر"}</option>
                        {CLASS_STATUS?.map((st) => (
                          <option key={st} value={st}>
                            {st}
                          </option>
                        ))}
                      </select>
                    ) : (
                      course?.status || "لا يوجد"
                    )}
                  </div>
                </div>

                {/* Dropdown actions */}
                <div className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-40 opacity-100" : "max-h-0 opacity-0"}`}>
                  <div
                    className={`rounded-2xl px-4 py-4 flex justify-evenly gap-2
                      ${darkMode ? "bg-white border border-black/10" : "bg-slate-300 border border-white/10"}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {!isEditing ? (
                      <button
                        type="button"
                        className="px-4 py-2 rounded-xl border border-black/10 bg-white hover:opacity-90 text-black"
                        onClick={(e) => {
                          e.stopPropagation();
                          startEdit(course);
                        }}
                      >
                        {language ? "Edit" : "تعديل"}
                      </button>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          type="button"
                          className="px-4 py-2 rounded-xl bg-green-600 text-white hover:opacity-90"
                          onClick={(e) => {
                            e.stopPropagation();
                            confirmEdit(course);
                          }}
                        >
                          {language ? "Confirm" : "تأكيد"}
                        </button>

                        <button
                          type="button"
                          className="px-4 py-2 rounded-xl border border-black/10 bg-white hover:opacity-90 text-black"
                          onClick={(e) => {
                            e.stopPropagation();
                            cancelEdit();
                          }}
                        >
                          {language ? "Cancel" : "إلغاء"}
                        </button>
                      </div>
                    )}

                    <button
                      type="button"
                      className="px-4 py-2 rounded-xl bg-red-600 text-white hover:opacity-90"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCourse(course);
                      }}
                    >
                      {language ? "Delete" : "حذف"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default EbdaEduWatomsCourseManagement;